import type { Attachment } from "svelte/attachments";

export interface ScrollGroupObserverOptions {
    /** Total number of items in the group. Used to detect the end of the list. */
    itemCount: number;
    /** Callback fired when the active index changes. */
    onActiveChange: (index: number) => void;
    /** Custom IntersectionObserver rootMargin */
    rootMargin?: string;
    /** If true, forces the last item to become active as soon as it enters the screen. */
    prioritizeLastItem?: boolean;
}

export function scrollGroupObserver(options: ScrollGroupObserverOptions) {
    // Holds the visibility state of all tracked elements
    const visibility: Record<number, boolean> = {};

    // A single, shared observer for performance
    let observer: IntersectionObserver | null = null;

    const getObserver = () => {
        if (typeof window === 'undefined') return null;

        if (!observer) {
            observer = new IntersectionObserver(
                (entries) => {
                    // 1. Update visibility map
                    entries.forEach((entry) => {
                        const target = entry.target as HTMLElement;
                        const index = Number(target.dataset.scrollIndex);
                        if (!isNaN(index)) {
                            visibility[index] = entry.isIntersecting;
                        }
                    });

                    // 2. Determine which items are currently on screen
                    const visible = Object.entries(visibility)
                        .filter(([_, isVisible]) => isVisible)
                        .map(([idx]) => Number(idx))
                        .sort((a, b) => a - b);

                    // 3. Apply active logic
                    if (visible.length > 0) {
                        if (options.prioritizeLastItem && visible.includes(options.itemCount - 1)) {
                            options.onActiveChange(options.itemCount - 1);
                        } else {
                            options.onActiveChange(visible[0]);
                        }
                    }
                },
                { rootMargin: options.rootMargin || '-20% 0px -20% 0px', threshold: 0 }
            );
        }
        return observer;
    };

    // Return the actual Attachment factory
    return (index: number): Attachment => {
        return (node: Element) => {
            const target = node as HTMLElement;

            // Tag the DOM node so the shared observer knows which index it corresponds to
            target.dataset.scrollIndex = index.toString();

            const obs = getObserver();
            obs?.observe(target);

            return () => {
                obs?.unobserve(target);
                delete visibility[index];
            };
        };
    };
}
